import { pantryColors } from '@pantry/ui'
import React, { useMemo, useState } from 'react'
import { useDebounce } from '../../../../hooks/useDebounce'
import type { LoyaltyCouponWebCategoryRef } from '../../../../types/loyaltyCoupon'
import { useLoyaltyCouponsContext } from '../hooks/useLoyaltyCouponsContext'

const p = pantryColors

const inputStyle: React.CSSProperties = {
  boxSizing: 'border-box',
  width: '100%',
  padding: '9px 12px',
  border: `1px solid ${p.gray[200]}`,
  borderRadius: 6,
  fontSize: 13,
  color: p.gray[800],
  background: p.white,
  outline: 'none',
}

const subLabel: React.CSSProperties = {
  fontSize: 11.5,
  fontWeight: 600,
  color: p.gray[500],
  marginBottom: 8,
}

interface Props {
  webCategories: LoyaltyCouponWebCategoryRef[]
  onWebCategoriesChange: (next: LoyaltyCouponWebCategoryRef[]) => void
}

/**
 * "Categories" tab body for the coupon modal's Applies-to section.
 *
 * Mirrors the HTML design (`breadstack-loyalty-pro.html` #couponModeCategories):
 * a single search input that filters the web-category list from the Categories
 * module, a result dropdown that excludes already-picked entries, and a chip
 * row beneath showing the current selection. The previous "5 first quick-pick
 * chips" UX was dropped — there's no equivalent in the design and it added
 * noise when the user already knew the category name.
 */
export const CouponModeCategories: React.FC<Props> = ({
  webCategories,
  onWebCategoriesChange,
}) => {
  const { webCategories: available, isWebCategoriesLoading: isLoading } =
    useLoyaltyCouponsContext()
  const [query, setQuery] = useState('')
  const debouncedQuery = useDebounce(query, 150)

  // Build the dropdown contents: exclude already-picked categories and, when
  // there's a search term, narrow by case-insensitive substring match. The
  // 8-row cap matches the products tab so both pickers feel the same.
  const pickedIds = useMemo(
    () => new Set(webCategories.map((w) => w.id)),
    [webCategories],
  )
  const results = useMemo(() => {
    const trimmed = debouncedQuery.trim().toLowerCase()
    const remaining = available.filter((w) => !pickedIds.has(w.id))
    const filtered = trimmed
      ? remaining.filter((w) => w.displayName.toLowerCase().includes(trimmed))
      : remaining
    return filtered.slice(0, 8)
  }, [available, pickedIds, debouncedQuery])

  const addWebCategory = (ref: LoyaltyCouponWebCategoryRef) => {
    if (pickedIds.has(ref.id)) return
    onWebCategoriesChange([...webCategories, ref])
    setQuery('')
  }

  const removeWebCategory = (id: string) => {
    onWebCategoriesChange(webCategories.filter((w) => w.id !== id))
  }

  return (
    <div>
      <div style={subLabel}>
        Pick categories{' '}
        <span style={{ color: p.gray[400], fontWeight: 500 }}>
          — suggestions from your Categories module
        </span>
      </div>
      <div style={{ position: 'relative' }}>
        <input
          style={inputStyle}
          type="text"
          placeholder="Type a category name…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoComplete="off"
        />
        {query && (
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              marginTop: 4,
              maxHeight: 220,
              overflowY: 'auto',
              background: p.white,
              border: `1px solid ${p.gray[200]}`,
              borderRadius: 8,
              boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)',
              zIndex: 2,
            }}
          >
            {isLoading ? (
              <div
                style={{
                  padding: '10px 12px',
                  fontSize: 12,
                  color: p.gray[500],
                }}
              >
                Loading categories…
              </div>
            ) : results.length === 0 ? (
              <div
                style={{
                  padding: '10px 12px',
                  fontSize: 12,
                  color: p.gray[500],
                }}
              >
                No categories match "{query}"
              </div>
            ) : (
              results.map((ref) => (
                <button
                  key={ref.id}
                  type="button"
                  onClick={() => addWebCategory(ref)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '8px 12px',
                    fontSize: 13,
                    color: p.gray[800],
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = p.gray[50]
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent'
                  }}
                >
                  {ref.displayName}
                </button>
              ))
            )}
          </div>
        )}
      </div>

      {webCategories.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 6,
            marginTop: 10,
          }}
        >
          {webCategories.map((w) => (
            <span
              key={w.id}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                padding: '4px 10px',
                background: p.brand.light,
                color: p.brand.primary,
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              {w.displayName}
              <button
                type="button"
                onClick={() => removeWebCategory(w.id)}
                aria-label={`Remove ${w.displayName}`}
                style={{
                  border: 'none',
                  background: 'transparent',
                  color: p.brand.primary,
                  fontSize: 14,
                  lineHeight: 1,
                  cursor: 'pointer',
                  padding: 0,
                }}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
